import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type EnvironmentVarnish, type VarnishOperation, type VarnishReading } from '../api';
import { useResource } from '../hooks';
import { Callout, Section } from './primitives';
import { FactList, QuietEmpty } from './project/ui';
import { relativeTime } from './project/projectHealth';
import { ConfirmDialog, ErrorNotice, LoadingRows, StatusBadge } from './ui';

const operationText: Record<VarnishOperation, { title: string; label: string; description: string }> = {
  verify: {
    title: 'Verify the page cache?',
    label: 'Verify cache',
    description:
      'Checks that Magento’s full page cache is Varnish, that Varnish reports Magento’s web server healthy, and that a page is a miss and then a hit. Nothing is purged.',
  },
  reload: {
    title: 'Reload the Varnish configuration?',
    label: 'Reload configuration',
    description:
      'Loads the live release’s VCL again — the one its Magento generated — after compiling it. Varnish keeps running and keeps its cache. A configuration that does not compile is never loaded.',
  },
  restart: {
    title: 'Restart Varnish?',
    label: 'Restart Varnish',
    description:
      'Restarts the Varnish service on its last validated configuration. The cache is emptied, and the store is served directly by Magento for the few seconds Varnish is down, so pages are slower until the cache refills.',
  },
};

function megabytes(bytes: number | undefined): string {
  if (bytes === undefined || !Number.isFinite(bytes)) return '';
  const mb = bytes / (1024 * 1024);
  if (mb < 1024) return `${Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${Number.isInteger(gb) ? gb : gb.toFixed(1)} GB`;
}

function percent(ratio: number | undefined): string {
  return ratio === undefined || ratio < 0 ? '' : `${(ratio * 100).toFixed(1)}%`;
}

/** The reading to show: one taken now, else the monitor's last result. */
function readingOf(view: EnvironmentVarnish): { reading?: VarnishReading; at?: string; source: 'live' | 'monitor' | 'none' } {
  if (view.live) return { reading: view.live, source: 'live' };
  const last = view.monitor?.last_result;
  if (last) return { reading: { status: last.status, message: last.message, metrics: last.metrics }, at: last.checked_at, source: 'monitor' };
  return { source: 'none' };
}

/**
 * VarnishCacheCard is a traditional Magento environment's Varnish full page
 * cache (CO-127): whether it serves, how much of the cache is used, how
 * often it answers from the cache, and which Magento release generated the
 * configuration it runs. The VCL is never written by Commerce Ops: each
 * release's own `bin/magento varnish:vcl:generate` produces it. Headless
 * projects do not have Varnish and the card renders nothing for them.
 */
export function VarnishCacheCard({
  environmentId,
  setupPath,
  onExecutionStarted,
}: {
  environmentId: string;
  /** Where "Plan again" lives (the project's Setup section). */
  setupPath: string;
  onExecutionStarted: (executionId: string) => void;
}) {
  const [live, setLive] = useState(false);
  const [reload, setReload] = useState(0);
  const view = useResource(() => api.getEnvironmentVarnish(environmentId, live), [environmentId, live, reload], environmentId, { key: `env:${environmentId}:varnish:${live}` });
  const [confirm, setConfirm] = useState<VarnishOperation | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function run(op: VarnishOperation) {
    setBusy(true);
    setError(null);
    try {
      const res = await api.runVarnishOperation(environmentId, op);
      setConfirm(null);
      onExecutionStarted(res.execution.id);
    } catch (cause) {
      setError(cause instanceof Error ? cause : new Error(String(cause)));
    } finally {
      setBusy(false);
    }
  }

  const data = view.data;
  if (data && !data.applies) return null;

  const title = 'Varnish full page cache';

  if (view.loading && !data) {
    return (
      <Section title={title} testId="varnish-card">
        <LoadingRows label="Reading Varnish" rows={5} rowHeight="22px" />
      </Section>
    );
  }
  if (view.error && !data) {
    return (
      <Section title={title} testId="varnish-card">
        <ErrorNotice error={view.error} />
      </Section>
    );
  }
  if (!data) return null;

  const active = data.state === 'active';
  const { reading, at, source } = readingOf(data);
  const m = reading?.metrics ?? {};
  const storageMB = data.storage_mb ?? 0;
  const used = m.storage_used_bytes;
  const lastVCL = data.last_vcl;
  const generated = (lastVCL?.result?.generated ?? undefined) as Record<string, unknown> | undefined;
  const failure = data.last_vcl_failure;
  const failureIsNewer = failure && (!lastVCL?.at || (failure.at && failure.at > lastVCL.at));
  const verification = data.last_verification;

  const actions = active ? (
    <span className="pill-list">
      <button type="button" className="button button--secondary button--small" onClick={() => (live ? setReload((v) => v + 1) : setLive(true))} disabled={view.loading} data-testid="varnish-read-now">
        {view.loading && live ? 'Reading' : 'Read now'}
      </button>
      <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('verify')} disabled={busy} data-testid="varnish-verify">
        Verify cache
      </button>
      <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('reload')} disabled={busy} data-testid="varnish-reload">
        Reload configuration
      </button>
      <button type="button" className="button button--secondary button--small" onClick={() => setConfirm('restart')} disabled={busy} data-testid="varnish-restart">
        Restart Varnish
      </button>
    </span>
  ) : null;

  return (
    <Section
      id={`varnish-card-${environmentId}`}
      title={title}
      testId="varnish-card"
      tone={reading?.status === 'critical' ? 'critical' : undefined}
      actions={actions}
    >
      <ErrorNotice error={error} />
      {view.error ? <ErrorNotice error={view.error} /> : null}

      {!active ? (
        <Callout
          tone={data.state === 'waiting_for_agent' ? 'warning' : 'info'}
          title={<StatusBadge kind="varnishState" value={data.state} testId="varnish-state" />}
          testId="varnish-not-active"
          actions={
            data.state === 'not_planned' || data.state === 'planned' ? (
              <Link className="button button--secondary button--small" to={setupPath}>
                {data.state === 'not_planned' ? 'Open setup to plan again' : 'Open setup'}
              </Link>
            ) : undefined
          }
        >
          {data.reason}
          {data.state === 'planned' && data.storage_mb ? ` The plan gives it ${data.storage_mb} MB of cache.` : ''}
        </Callout>
      ) : (
        <>
          {reading?.message ? (
            <p className="varnish-card__status" data-testid="varnish-reading" aria-live="polite">
              <StatusBadge kind="check" value={reading.status ?? 'unknown'} />
              <span>{reading.message}</span>
              <span className="muted">{source === 'live' ? 'read now' : at ? `checked ${relativeTime(at)}` : ''}</span>
            </p>
          ) : (
            <QuietEmpty testId="varnish-no-reading">
              No Varnish check has run yet
            </QuietEmpty>
          )}
          {failureIsNewer && failure ? (
            <Callout tone="danger" title="The last configuration was not loaded" testId="varnish-vcl-failure">
              <p className="varnish-card__error">{failure.message || 'The VCL did not validate.'}</p>
              <p>
                Varnish kept the configuration it was running
                {lastVCL?.result?.releaseTag ? ` (release ${String(lastVCL.result.releaseTag)})` : ''}. Fix the release’s Magento Varnish
                setup and deploy again, or reload the configuration of the live release.
              </p>
            </Callout>
          ) : null}
          <div className="varnish-card__grid">
            <FactList
              facts={[
                { label: 'Hit ratio', value: m.hit_ratio !== undefined ? <span data-testid="varnish-hit-ratio">{percent(m.hit_ratio)}</span> : <span className="muted">no cacheable request yet</span> },
                {
                  label: 'Requests',
                  value:
                    m.cache_hits !== undefined ? `${(m.cache_hits ?? 0).toLocaleString()} hits, ${(m.cache_misses ?? 0).toLocaleString()} misses` : <span className="muted">not read yet</span>,
                },
                {
                  label: 'Cache size',
                  value: (
                    <span data-testid="varnish-storage">
                      {storageMB ? megabytes(storageMB * 1024 * 1024) : 'default'}
                      {used !== undefined ? `, ${megabytes(used)} used (${percent(m.storage_used_ratio)})` : ''}
                    </span>
                  ),
                },
                { label: 'Cached pages', value: m.objects !== undefined ? m.objects.toLocaleString() : <span className="muted">not read yet</span> },
                {
                  label: 'Evicted to make room',
                  value: m.lru_nuked !== undefined ? m.lru_nuked.toLocaleString() : <span className="muted">not read yet</span>,
                },
                {
                  label: 'Backend',
                  value: (
                    <span>
                      <code>{data.backend}</code>{' '}
                      {m.backend_healthy === undefined ? null : m.backend_healthy ? <StatusBadge kind="check" value="healthy" label="Healthy" /> : <StatusBadge kind="check" value="critical" label="Unhealthy" />}
                    </span>
                  ),
                },
              ]}
            />
            <FactList
              facts={[
                { label: 'Version', value: data.version ? `Varnish ${data.version}` : <span className="muted">not known</span> },
                { label: 'Configuration source', value: <span data-testid="varnish-vcl-source">Generated by Magento</span> },
                {
                  label: 'Generated for',
                  value: generated?.magentoVersion ? (
                    `Magento ${String(generated.magentoVersion)}${lastVCL?.result?.releaseTag ? `, release ${String(lastVCL.result.releaseTag)}` : ''}`
                  ) : data.active_release ? (
                    `Release ${data.active_release}`
                  ) : (
                    <span className="muted">no release yet</span>
                  ),
                },
                {
                  label: 'Last loaded',
                  value: lastVCL?.at ? (
                    `${relativeTime(lastVCL.at)}${lastVCL.result?.mode === 'hot_reload' ? ', without a restart' : lastVCL.result?.mode === 'started' ? ', on start' : ''}`
                  ) : (
                    <span className="muted">not recorded</span>
                  ),
                },
                {
                  label: 'Matches the live release',
                  value:
                    m.vcl_matches_release === undefined ? <span className="muted">not read yet</span> : m.vcl_matches_release ? 'Yes' : <StatusBadge kind="check" value="warning" label="No — reload the configuration" />,
                },
                {
                  label: 'Last verification',
                  value: verification ? (
                    <span>
                      <StatusBadge kind="check" value={verification.status === 'succeeded' ? 'healthy' : 'critical'} label={verification.status === 'succeeded' ? 'Passed' : 'Failed'} />{' '}
                      <span className="muted">{relativeTime(verification.at)}</span>
                    </span>
                  ) : (
                    <span className="muted">not run yet</span>
                  ),
                },
              ]}
            />
          </div>
          {verification?.status === 'failed' && verification.message ? (
            <p className="type-meta" data-testid="varnish-verification-message">
              {verification.message}
            </p>
          ) : null}
        </>
      )}

      {confirm ? (
        <ConfirmDialog
          open
          title={operationText[confirm].title}
          description={operationText[confirm].description}
          confirmLabel={operationText[confirm].label}
          busy={busy}
          onConfirm={() => run(confirm)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}
    </Section>
  );
}
